import { useMemo, useEffect, useRef } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select.js';
import { formatPlainTime, WrappedPlainTimeInterval } from '@airreading/common';
import { Temporal } from 'temporal-polyfill';
import { MultiSelectOption } from '@/components/ui/multi-select.js';
import clsx from 'clsx';
import { FormInputTimeColumnsProps } from './form-input-time-utils.js';

export const FormInputTimeSelect = ({
  value,
  onChange,
  step,
  min,
  max,
  className,
  addMax,
  footerAction,
}: FormInputTimeColumnsProps) => {
  const selectTriggerRef = useRef<HTMLButtonElement>(null);
  const validOptions = useMemo(() => {
    try {
      const options = Array.from(new WrappedPlainTimeInterval(min, max).step(step)).map(
        (time): MultiSelectOption => ({
          label: formatPlainTime(time, { hour12: true }),
          value: time.toString(),
        }),
      );
      if (addMax) {
        const elevenFiftyNine = Temporal.PlainTime.from(max.toString());
        const alreadyExists = options.some((option) => option.value === elevenFiftyNine.toString());
        if (!alreadyExists) {
          options.push({
            label: formatPlainTime(elevenFiftyNine, { hour12: true }),
            value: elevenFiftyNine.toString(),
          });
        }
      }
      return options;
    } catch {
      return [];
    }
  }, [step, min, max]);

  const handleChange = (value: string) => {
    const plainTime = Temporal.PlainTime.from(value);
    onChange(plainTime);
  };

  const defaultValue = value ? value.toString() : '';

  // Auto-open the select when the component mounts (when popover opens)
  useEffect(() => {
    const timer = setTimeout(() => {
      if (selectTriggerRef.current) {
        selectTriggerRef.current.click();
      }
    }, 0);

    return () => clearTimeout(timer);
  }, []);

  return (
    <Select value={defaultValue} onValueChange={handleChange}>
      <SelectTrigger ref={selectTriggerRef} className="w-36">
        <SelectValue placeholder="Pick a time" />
      </SelectTrigger>
      <SelectContent className="max-h-60 w-36 overflow-y-auto">
        {validOptions.map((option) => (
          <SelectItem
            key={option.value}
            value={option.value}
            className={clsx('tabular-nums relative p-1 flex justify-center [&>span]:hidden', className)}
          >
            {option.label}
          </SelectItem>
        ))}
        {footerAction && (
          <div className="border-t pt-2 mt-2 px-2">
            <button
              type="button"
              className="w-full text-center text-xs text-blue-600 hover:underline"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                footerAction.onClick({ closePopover: () => {} });
              }}
            >
              {footerAction.label}
            </button>
          </div>
        )}
      </SelectContent>
    </Select>
  );
};
